/**
 * Recover from a stale/failed dynamic-import instead of showing a 500.
 * -------------------------------------------------------------------
 * "Failed to fetch dynamically imported module" happens two ways:
 *   • PROD — a deploy renamed the hashed chunks; a tab opened before it 404s
 *     the lazy-loaded module on the next navigation.
 *   • DEV  — Vite re-optimizes deps mid-session and invalidates the module
 *     URLs the page still holds (it can cascade to unrelated core modules).
 *
 * The fix in both cases is the same: reload once to fetch fresh chunk refs.
 * `experimental.emitRouteChunkError: 'automatic'` covers navigation-triggered
 * errors; this catches the rest (the app:chunkError hook + the Vue Router
 * error handler).
 *
 * LOOP GUARD: only reload if we haven't already reloaded for this reason in
 * the last few seconds — so a genuinely broken build fails visibly instead of
 * reloading forever.
 */

const RELOAD_KEY = 'chunkReloadAt'
const RELOAD_COOLDOWN_MS = 10_000

function looksLikeChunkError(error) {
  const message = String(error?.message ?? error ?? '')

  return (
    /dynamically imported module/i.test(message) ||
    /Failed to fetch/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message)
  )
}

function reloadOnce() {
  let last = 0
  try {
    last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0
  }
  catch {
    // sessionStorage blocked (private mode) — fall through and reload anyway.
  }

  if (Date.now() - last < RELOAD_COOLDOWN_MS) return false

  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  }
  catch {
    // Non-fatal — the reload below is what matters.
  }

  window.location.reload()

  return true
}

export default defineNuxtPlugin(nuxtApp => {
  nuxtApp.hook('app:chunkError', ({ error }) => {
    if (looksLikeChunkError(error)) reloadOnce()
  })

  nuxtApp.$router.onError(error => {
    if (looksLikeChunkError(error)) reloadOnce()
  })

  // A successful navigation means the app is healthy again — clear the guard
  // so a LATER, unrelated chunk error still gets its one reload.
  nuxtApp.hook('page:finish', () => {
    try {
      sessionStorage.removeItem(RELOAD_KEY)
    }
    catch {
      // Ignore.
    }
  })
})
